import { BrandedQRCode } from "@/components/StyledQRCode/BrandedQRCode";
import { Skeleton } from "@/components/ui/skeleton";
import { Agent } from "@/types/agent/agent";
import { ConsoleBand, ReachBandData } from "@/types/dashboard/genie-console";
import { EmptyPreview, PreviewError } from "./previewShell";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ReachPreviewProps {
  band: ConsoleBand<ReachBandData>;
  brandColor: string;
  genie: Agent | null;
}

/**
 * REACH band preview — the user's ACTUAL QR code (the same
 * `BrandedQRCode` the QR modal and activation packs render, pointed at their
 * real public link) beside their real channel chips and real pack names.
 */
export const ReachPreview = ({ band, brandColor, genie }: ReachPreviewProps) => {
  if (band.isError) return <PreviewError label="reach" />;

  if (band.isLoading) {
    return (
      <div className="flex flex-col gap-4 sm:flex-row">
        <Skeleton className="h-[124px] w-[124px] flex-shrink-0 rounded-xl" />
        <div className="flex-1 space-y-2">
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-9 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const { qrValue, channels, packs, scanCount } = band.data;

  if (!genie) {
    return (
      <EmptyPreview
        message="Create a Genie and you'll get a link, a QR code, and a website widget for it."
        actionLabel="Create a Genie"
        to="/user-hub/genie-setup"
        brandColor={brandColor}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
      {/* The genie's real QR code, at the size a preview needs. */}
      <div className="flex flex-shrink-0 flex-col items-center gap-1.5">
        {qrValue ? (
          <div className="rounded-xl border border-gray-100 bg-white p-1">
            <BrandedQRCode
              value={qrValue}
              size={96}
              genie={genie}
              displayConfig={{
                style: "minimal",
                label: "none",
                transparentBackground: false,
                showLogo: true,
                foregroundColor: genie.branding?.qrCodeStyle?.foregroundColor || "#000000",
                backgroundColor: genie.branding?.qrCodeStyle?.backgroundColor || "#FFFFFF",
              }}
            />
          </div>
        ) : (
          <div className="flex h-[124px] w-[124px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white px-3 text-center text-xs text-gray-500">
            Set a link name to get a QR code
          </div>
        )}
        <p className="text-[11px] font-medium text-gray-500">
          {scanCount > 0
            ? `${scanCount} ${scanCount === 1 ? "scan" : "scans"}`
            : "No scans yet"}
        </p>
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        {channels.map((channel) => (
          <div
            key={channel.id}
            className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2"
          >
            <span
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
              style={{
                backgroundColor: channel.live ? `${brandColor}14` : "#F3F4F6",
              }}
            >
              <GenieIcon
                name={channel.genieIcon}
                size={15}
                color={channel.live ? brandColor : "#9CA3AF"}
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-semibold text-gray-900">{channel.label}</span>
              <span className="block truncate text-[11px] text-gray-500">{channel.detail}</span>
            </span>
            <span
              className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={
                channel.live
                  ? { backgroundColor: `${brandColor}1a`, color: brandColor }
                  : { backgroundColor: "#F3F4F6", color: "#6B7280" }
              }
            >
              {channel.live ? "Live" : "Off"}
            </span>
          </div>
        ))}

        {packs.length > 0 && (
          <p className="truncate pt-0.5 text-xs text-gray-500">
            Packs: {packs.map((pack) => pack.name).join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReachPreview;
